import type { ApprovalRow } from '../../lib/database.types'
import { at, arr, obj, str, num, bool } from '../../lib/json'
import type { Json } from '../../lib/database.types'
import { age } from '../../lib/time'
import { humanise, short, stripMarkers, stripMarkersOrNull } from '../../lib/text'

/**
 * `approvals.context`, as the fleet actually writes it.
 *
 * There are four shapes in the table today and they share almost nothing below
 * the top level: a code review carries `review{criteria,comments,blocking}`, a
 * data-quality report carries `checks[]` and `totals`, a drafted spec carries
 * `draft{criteria[] of strings}` and `premise{cites}`, and a failed run carries
 * `failure{}`. More will arrive; the finding lanes are next.
 *
 * So the adapters below are selected by the STRUCTURE that is present, never by
 * the `kind` string. `kind === 'dq_report' ? ...` is the same mistake as
 * `dept === 'dev' ? ...`, one level down: it means the fifth shape renders as
 * an empty screen instead of a partial one, and the dashboard silently stops
 * showing a department's work.
 *
 * Everything degrades. An unrecognised context still yields a title, a summary
 * and whatever scalars it carries, because a row a person cannot read is worse
 * than a row that is merely thin.
 */

export type DecisionState = 'pending' | 'approved' | 'rejected' | 'dismissed'

/**
 * Something the fleet evaluated and got a result for: a review criterion, a
 * data-quality check. `ok` is null when the fleet did not say.
 */
export interface Assertion {
  label: string
  ok: boolean | null
  note: string | null
  /** "9 rows" -- the size of the problem, when the shape carries one. */
  magnitude: string | null
  /** Concrete examples, already a sample. Detail view only, never the queue. */
  samples: string[]
  /**
   * The same rows with far less truncation, for the briefing. The screen has a
   * column width to respect; a session reading an error does not, and the part
   * that gets cut at screen width -- a url, an id -- is the part it needs.
   */
  samplesFull: string[]
  sampleNote: string | null
}

/** Something proposed but not yet evaluated -- a drafted spec's criteria. */
export interface ProposalGroup {
  heading: string
  items: string[]
}

/** A premise the agent checked before trusting it: what it claimed, and how. */
export interface Evidence {
  claim: string
  found: string | null
  checked: string | null
}

export interface Failure {
  error: string
  permanent: boolean | null
  attemptsUsed: number | null
  attemptCap: number | null
}

/** A scalar the context carried that this app has no specific opinion about. */
export interface Fact {
  label: string
  value: string
}

/**
 * A report waiting to be sent: what the approver opens, who it goes to, and how
 * old its numbers are. Unlike every other shape here, approving this one IS the
 * gate -- the lane sends nothing until a person says yes -- so the screen has to
 * carry enough to say yes to.
 */
export interface Distribution {
  /** The report itself, not a page about it. */
  artifactUrl: string | null
  recipients: string[]
  channel: string | null
  dataAsOf: string | null
  freshnessCheckedAt: string | null
  /** "distribution 1 of 4" -- which of the gated sends this is, when the context says. */
  number: number | null
  of: number | null
}

export interface ReviewComment {
  summary: string
  detail: string | null
  cites: string | null
  category: string | null
  tier: number | null
}

export interface QueueItem {
  id: string
  runId: string | null
  department: string
  source: string
  title: string
  summary: string | null
  refUrl: string | null
  state: DecisionState
  decidedBy: string | null
  decidedAt: string | null
  decisionNote: string | null
  createdAt: string
  age: string

  /** Display noun for the chip, as the fleet named it. */
  kind: string | null
  schemaVersion: number | null
  repo: string | null
  ticketRef: string | null
  ticketUrl: string | null
  buildBranch: string | null
  buildPrUrl: string | null
  buildRunId: string | null
  /** Above 1 means the build was retried before it got here. */
  attempt: number | null

  verdict: string | null
  /** Results, from whichever shape carried them. */
  assertions: Assertion[]
  /** What the matched adapter called them, for honest wording. */
  assertionNoun: string | null
  proposals: ProposalGroup[]
  evidence: Evidence[]
  failure: Failure | null
  distribution: Distribution | null
  facts: Fact[]
  comments: ReviewComment[]
  blocking: ReviewComment[]
  demoted: ReviewComment[]

  /**
   * The plain-language summary a drafted spec carries beside its technical one.
   * Its own line on the row and its own block in the detail view: it is the
   * part of a drafted spec someone can read without reading the criteria, and
   * it arrived folded into the head of `summary` where it read as a run-on.
   */
  plain: string | null

  /**
   * One line for the queue row. The queue is not where detail lives (rule 3),
   * so the criteria collapse to a count and the reading happens in the detail
   * view.
   */
  headline: string
}

function toComment(value: unknown): ReviewComment | null {
  const o = obj(value as never)
  if (!o) return null
  const summary = str(o['summary']) ?? str(o['detail'])
  if (!summary) return null
  return {
    summary: stripMarkers(summary),
    detail: str(o['detail']) === summary ? null : stripMarkersOrNull(str(o['detail'])),
    cites: str(o['cites']),
    category: str(o['category']),
    tier: num(o['tier']),
  }
}

/** One sample row rendered as text. They are arbitrary objects from the fleet. */
function sampleLine(value: Json, cellMax: number, lineMax: number): string | null {
  const row = obj(value)
  if (!row) return typeof value === 'string' ? short(value, lineMax) : null
  const pairs: string[] = []
  for (const [key, cell] of Object.entries(row)) {
    if (cell === null || cell === undefined) continue
    if (typeof cell === 'object') continue
    pairs.push(`${key} ${short(String(cell), cellMax)}`)
  }
  return pairs.length > 0 ? short(pairs.join(' · '), lineMax) : null
}

function samplesFrom(
  value: Json | undefined,
  rowsFailing: number | null,
): { samples: string[]; samplesFull: string[]; note: string | null } {
  const rows = arr(value)
  if (rows.length === 0) return { samples: [], samplesFull: [], note: null }
  const taken = rows.slice(0, 3)
  const samples = taken.map((row) => sampleLine(row, 60, 220)).filter((line): line is string => line !== null)
  const samplesFull = taken.map((row) => sampleLine(row, 400, 1200)).filter((line): line is string => line !== null)
  // The fleet already sampled; saying so stops anyone reading five rows as the
  // whole problem when nine rows failed.
  const note =
    rowsFailing !== null && rowsFailing > rows.length
      ? `${rows.length} of ${rowsFailing} shown`
      : rows.length > samples.length
        ? `${samples.length} of ${rows.length} shown`
        : null
  return { samples, samplesFull, note }
}

/**
 * Adapter A -- results as objects carrying a `criterion` and a `met`.
 * Written by the review agent.
 */
function readCriteria(ctx: Json): { assertions: Assertion[]; noun: string } | null {
  const rows = arr(at(ctx, 'review', 'criteria'))
  if (rows.length === 0) return null
  const assertions: Assertion[] = []
  for (const row of rows) {
    const o = obj(row)
    const label = o && str(o['criterion'])
    if (!label) continue
    assertions.push({
      label: stripMarkers(label),
      ok: o ? bool(o['met']) : null,
      note: stripMarkersOrNull(o ? str(o['note']) : null),
      magnitude: null,
      samples: [],
      samplesFull: [],
      sampleNote: null,
    })
  }
  return assertions.length > 0 ? { assertions, noun: 'criteria' } : null
}

/**
 * Adapter B -- results as objects carrying a `check_name` and a `status`.
 * Written by the data-quality reports. Failing checks are already ordered
 * first by the producer and that order is preserved.
 */
function readChecks(ctx: Json): { assertions: Assertion[]; noun: string } | null {
  const rows = arr(at(ctx, 'checks'))
  if (rows.length === 0) return null
  const assertions: Assertion[] = []
  for (const row of rows) {
    const o = obj(row)
    const label = o && (str(o['check_name']) ?? str(o['name']))
    if (!o || !label) continue
    const status = str(o['status'])
    const rowsFailing = num(o['rows_failing'])
    const { samples, samplesFull, note } = samplesFrom(o['sample'], rowsFailing)
    assertions.push({
      label,
      // Only 'pass' passes and only 'fail' fails. Anything else -- 'degraded',
      // 'skipped', whatever arrives next -- reads as unknown, because guessing
      // either way misreports it and this column is what a person acts on.
      ok: status === 'pass' ? true : status === 'fail' ? false : null,
      note: stripMarkersOrNull(str(o['error'])),
      magnitude: rowsFailing !== null && rowsFailing > 0 ? `${rowsFailing} ${rowsFailing === 1 ? 'row' : 'rows'}` : null,
      samples,
      samplesFull,
      sampleNote: note,
    })
  }
  return assertions.length > 0 ? { assertions, noun: 'checks' } : null
}

/** Adapter C -- lists of plain strings that were proposed, not evaluated. */
function readProposals(ctx: Json): ProposalGroup[] {
  const groups: ProposalGroup[] = []
  for (const [path, heading] of [
    [['draft', 'criteria'], 'Proposed acceptance criteria'],
    [['draft', 'out_of_scope'], 'Out of scope'],
  ] as const) {
    const items = arr(at(ctx, ...path))
      .map((entry) => str(entry))
      .filter((entry): entry is string => entry !== null)
      .map(stripMarkers)
    if (items.length > 0) groups.push({ heading, items })
  }
  return groups
}

/** Adapter D -- premises the agent verified instead of assuming. */
function readEvidence(ctx: Json): Evidence[] {
  const out: Evidence[] = []
  for (const row of arr(at(ctx, 'premise', 'cites'))) {
    const o = obj(row)
    const claim = o && str(o['claim'])
    if (!claim) continue
    out.push({
      claim: stripMarkers(claim),
      found: stripMarkersOrNull(str(o['found'])),
      checked: str(o['checked']) ? stripMarkers(str(o['checked'])!) : null,
    })
  }
  return out
}

/** Adapter E -- a run that could not finish. */
function readFailure(ctx: Json): Failure | null {
  const f = obj(at(ctx, 'failure'))
  const error = f && str(f['error'])
  if (!f || !error) return null
  return {
    error,
    permanent: bool(f['permanent']),
    attemptsUsed: num(f['attempts_used']),
    attemptCap: num(f['attempt_cap']),
  }
}

/**
 * Adapter G -- something about to be sent. Any context carrying a `report`
 * block with a link or a recipient list is one, whatever its kind; the
 * distribution count sits at the root because that is where scalars go.
 */
function readDistribution(ctx: Json): Distribution | null {
  const r = obj(at(ctx, 'report'))
  if (!r) return null
  const artifactUrl = str(r['artifact_url'])
  const recipients = arr(r['recipients'])
    .map((entry) => str(entry))
    .filter((entry): entry is string => entry !== null && entry.trim() !== '')
  if (!artifactUrl && recipients.length === 0) return null
  return {
    artifactUrl,
    recipients,
    channel: str(r['channel']),
    dataAsOf: str(r['data_as_of']),
    freshnessCheckedAt: str(r['freshness_checked_at']),
    number: num(at(ctx, 'distribution')),
    of: num(at(ctx, 'approval_required_through')),
  }
}

/**
 * Adapter F -- the plain-language summary, read by structure like everything
 * else here. Any context carrying a string at `draft.plain` has one, whatever
 * its `kind` says it is.
 */
function readPlain(ctx: Json): string | null {
  const plain = str(at(ctx, 'draft', 'plain'))
  if (!plain) return null
  // A string that was nothing but markers is not a summary.
  return stripMarkers(plain).trim() || null
}

/**
 * Whatever writes `draft.plain` also folds a copy of it into the head of
 * `summary`, ahead of the mechanics, and the copy is sometimes cut short.
 * Rendered as written the row says the same sentence twice and then runs
 * straight into "Waiting in Spec Draft -- ..." as one line, so when the
 * summary opens with a leading fragment of `plain` the fragment goes and the
 * mechanics become the summary.
 *
 * The fragment has to be a real one: the whole of `plain`, or at least
 * `MIN_FRAGMENT` characters of it, ending where a cut plausibly ends -- at an
 * ellipsis, a space or a mark. Two sentences that merely start alike are a
 * coincidence, and chopping four characters off a summary nobody folded is
 * worse than showing the duplicate. Nothing about the exact truncation is
 * known from here, which is why this tolerates any fragment and leaves the
 * summary alone when none matches.
 */
const MIN_FRAGMENT = 16

function withoutPlainPrefix(summary: string | null, plain: string | null): string | null {
  if (!summary || !plain) return summary
  const flat = summary.replace(/\s+/g, ' ').trim()
  const full = plain.replace(/\s+/g, ' ').trim()
  let matched = 0
  while (matched < flat.length && matched < full.length && flat[matched] === full[matched]) matched += 1
  const whole = matched === full.length
  if (!whole && matched < MIN_FRAGMENT) return summary
  const rest = flat.slice(matched)
  // Mid-word with nothing to mark the cut, and `plain` continuing past it, is
  // two strings that agreed for a while rather than a fold.
  if (!whole && rest && !/^[\s\p{P}]/u.test(rest)) return summary
  return rest.replace(/^[\s\p{P}]+/u, '') || null
}

/**
 * Scalars the context carried, harvested generically rather than named. A new
 * shape's `project`, `ran_at` or `base_commit` shows up in the rail without
 * anyone adding a line here.
 */
const NOT_A_FACT = new Set(['kind', 'schema', 'run_id', 'summary', 'title'])

function readFacts(ctx: Json): Fact[] {
  const facts: Fact[] = []
  const push = (key: string, value: Json | undefined) => {
    if (NOT_A_FACT.has(key)) return
    if (typeof value === 'string' && value.trim()) facts.push({ label: humanise(key), value: short(value, 80) })
    else if (typeof value === 'number') facts.push({ label: humanise(key), value: String(value) })
    else if (typeof value === 'boolean') facts.push({ label: humanise(key), value: value ? 'yes' : 'no' })
  }
  const root = obj(ctx)
  if (root) for (const [key, value] of Object.entries(root)) push(key, value)
  const totals = obj(at(ctx, 'totals'))
  if (totals) for (const [key, value] of Object.entries(totals)) push(key, value)
  const draft = obj(at(ctx, 'draft'))
  if (draft) for (const key of ['base_branch', 'base_commit']) push(key, draft[key])
  return facts
}

function headlineFor(item: {
  assertions: Assertion[]
  assertionNoun: string | null
  blocking: ReviewComment[]
  comments: ReviewComment[]
  proposals: ProposalGroup[]
  evidence: Evidence[]
  failure: Failure | null
  distribution: Distribution | null
  /** Whether this shape has a notion of blocking at all. */
  hasBlockingConcept: boolean
}): string {
  const parts: string[] = []
  const noun = item.assertionNoun ?? 'checks'

  if (item.failure) {
    parts.push(
      item.failure.attemptsUsed !== null
        ? `failed after ${item.failure.attemptsUsed} ${item.failure.attemptsUsed === 1 ? 'attempt' : 'attempts'}`
        : 'failed',
    )
  }

  if (item.distribution) {
    const d = item.distribution
    const n = d.recipients.length
    if (n > 0) parts.push(`to ${n} ${n === 1 ? 'recipient' : 'recipients'}${d.channel ? ` by ${d.channel}` : ''}`)
    if (d.number !== null) parts.push(`distribution ${d.number}${d.of !== null ? ` of ${d.of}` : ''}`)
  }

  if (item.assertions.length > 0) {
    const failing = item.assertions.filter((a) => a.ok === false).length
    const met = item.assertions.filter((a) => a.ok === true).length
    // Lead with the failures when there are any: that is what the person is
    // being asked about. Stated as a count, not styled as an alarm.
    parts.push(
      failing > 0
        ? `${failing} of ${item.assertions.length} ${noun} failing`
        : `${met} of ${item.assertions.length} ${noun} met`,
    )
    const rows = item.assertions.reduce((total, a) => total + (a.ok === false ? Number(a.magnitude?.split(' ')[0] ?? 0) : 0), 0)
    if (rows > 0) parts.push(`${rows.toLocaleString('en-GB')} rows affected`)
  }

  if (item.proposals.length > 0) {
    const count = item.proposals[0]?.items.length ?? 0
    if (count > 0) parts.push(`${count} criteria proposed`)
  }
  if (item.evidence.length > 0) {
    parts.push(`${item.evidence.length} ${item.evidence.length === 1 ? 'premise' : 'premises'} checked`)
  }
  if (item.blocking.length > 0) {
    parts.push(`${item.blocking.length} blocking ${item.blocking.length === 1 ? 'finding' : 'findings'}`)
  } else if (
    item.hasBlockingConcept &&
    item.assertions.length > 0 &&
    !item.failure &&
    // "nothing blocking" beside three failing checks is a contradiction. Only
    // a shape with a blocking list, and nothing failing, gets the reassurance.
    item.assertions.every((a) => a.ok !== false)
  ) {
    parts.push('nothing blocking')
  }
  if (item.comments.length > 0) {
    parts.push(`${item.comments.length} ${item.comments.length === 1 ? 'note' : 'notes'}`)
  }
  return parts.join(' \u00b7 ')
}

export function toQueueItem(row: ApprovalRow, now = Date.now()): QueueItem {
  const ctx = row.context
  const comments = arr(at(ctx, 'review', 'comments')).map(toComment).filter((c): c is ReviewComment => c !== null)
  const blocking = arr(at(ctx, 'review', 'blocking')).map(toComment).filter((c): c is ReviewComment => c !== null)
  const demoted = arr(at(ctx, 'review', 'demoted')).map(toComment).filter((c): c is ReviewComment => c !== null)

  // First adapter whose structure is present wins. Order is not precedence --
  // no context carries two of these -- it is just a fixed sequence.
  const results = readCriteria(ctx) ?? readChecks(ctx)
  const proposals = readProposals(ctx)
  const plain = readPlain(ctx)
  const evidence = readEvidence(ctx)
  const failure = readFailure(ctx)
  const distribution = readDistribution(ctx)

  const state = (['pending', 'approved', 'rejected', 'dismissed'] as const).includes(row.state as DecisionState)
    ? (row.state as DecisionState)
    : 'pending'

  const shared = {
    assertions: results?.assertions ?? [],
    assertionNoun: results?.noun ?? null,
    proposals,
    evidence,
    failure,
    distribution,
    comments,
    blocking,
    demoted,
  }
  const hasBlockingConcept = obj(at(ctx, 'review')) !== null

  return {
    id: row.id,
    runId: row.run_id,
    department: row.department,
    source: row.source,
    title: row.title,
    summary: withoutPlainPrefix(row.summary, plain),
    refUrl: row.ref_url,
    state,
    decidedBy: row.decided_by,
    decidedAt: row.decided_at,
    decisionNote: row.decision_note,
    createdAt: row.created_at,
    age: age(row.created_at, now),

    kind: str(at(ctx, 'kind')),
    schemaVersion: num(at(ctx, 'schema')),
    repo: str(at(ctx, 'repo')),
    ticketRef: str(at(ctx, 'ticket', 'ref')),
    ticketUrl: str(at(ctx, 'ticket', 'url')),
    buildBranch: str(at(ctx, 'build', 'branch')),
    buildPrUrl: str(at(ctx, 'build', 'pr_url')),
    buildRunId: str(at(ctx, 'build', 'run_id')),
    attempt: num(at(ctx, 'build', 'attempt')),

    verdict: str(at(ctx, 'review', 'verdict')),
    plain,
    facts: readFacts(ctx),
    ...shared,
    headline: headlineFor({ ...shared, hasBlockingConcept }),
  }
}

/** Underscores are how the fleet writes a kind; they are not how people read one. */
export function readableKind(kind: string | null): string | null {
  return kind ? kind.replace(/[_-]+/g, ' ') : null
}

/** A decision nobody made by hand. Shown as such rather than as a person. */
export const isAutomated = (item: QueueItem): boolean => (item.decidedBy ?? '').startsWith('system:')

/* ---------------------------------------------------------------------------
 * Interface vocabulary.
 *
 * The queue is triage, not authorisation. Approving an item authorises nothing
 * and never did -- the row changes state and an event is written. So the words
 * people see are open / closed / dismissed, and the verbs are close and
 * dismiss.
 *
 * The table keeps its own vocabulary. Renaming `approvals` and its states would
 * be a migration plus two repos plus the contract file, for a word. A gap
 * between schema language and interface language is normal and cheap; this
 * module is the whole width of it, and nothing outside it should know that
 * "close" writes 'approved'.
 * ------------------------------------------------------------------------- */

/** What Close writes. The verb is new; the stored value is not. */
export const CLOSE_STATE: DecisionState = 'approved'
export const DISMISS_STATE: DecisionState = 'dismissed'
export const REOPEN_STATE: DecisionState = 'pending'

export interface StateLabel {
  /** "open" / "closed" / "dismissed" */
  label: string
  /** Whether the item still wants a person. */
  open: boolean
}

export function stateLabel(state: DecisionState): StateLabel {
  switch (state) {
    case 'pending':
      return { label: 'open', open: true }
    case 'approved':
      return { label: 'closed', open: false }
    case 'dismissed':
      return { label: 'dismissed', open: false }
    case 'rejected':
      // Nothing in the interface writes this any more -- there is no reject
      // verb. Rows that already carry it are shown as they are rather than
      // being folded into "closed", which would restate someone's judgement.
      return { label: 'rejected', open: false }
  }
}

export const isOpen = (item: QueueItem): boolean => stateLabel(item.state).open

/**
 * The words on the buttons, per item.
 *
 * Close and dismiss are right for triage, where deciding authorises nothing.
 * A distribution is the one shape where it does: the state Close writes is the
 * yes the lane waits for, and anything else cancels the send. Labelled "Close",
 * that reads as "make this go away"; so for an item carrying a distribution the
 * same two states are named for what they do. Selected by structure, like the
 * adapters, and still nothing outside this module knows which state is which.
 */
export interface Verbs {
  close: string
  dismiss: string
  /** Placeholder for the note asked for alongside dismiss. */
  dismissPrompt: string
  /** What a decided item reads as, in place of stateLabel's word. */
  decided: string
  /** Name for refUrl when it is shown as a link. */
  refLink: string
}

export function verbsFor(item: QueueItem): Verbs {
  if (item.distribution) {
    return {
      close: 'Approve & send',
      dismiss: "Don't send",
      dismissPrompt: 'Why not send it? — kept with the item, and the run is cancelled',
      decided:
        item.state === 'approved' ? 'approved to send'
        : item.state === 'dismissed' ? 'not sent'
        : stateLabel(item.state).label,
      refLink: 'Report',
    }
  }
  return {
    close: 'Close',
    dismiss: 'Dismiss',
    dismissPrompt: 'Why is this not a problem? — kept with the item',
    decided: stateLabel(item.state).label,
    // Neutral would be better, but every item with a ref_url today is a pull request.
    refLink: 'Pull request',
  }
}
